/**
 * Manual ordering with fractional-index keys. Keys compare byte by byte
 * (never localeCompare), the same as Postgres COLLATE "C" on the server.
 */
import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";

export function compareKeys(a: string, b: string) {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function bySortKey<T extends { sortKey: string; id: string }>(a: T, b: T) {
  return compareKeys(a.sortKey, b.sortKey) || compareKeys(a.id, b.id);
}

/** A key between two neighbours (either may be missing). */
export function keyBetween(before?: string | null, after?: string | null): string {
  try {
    return generateKeyBetween(before ?? null, after ?? null);
  } catch {
    // Neighbours share a key (concurrent inserts) or are out of order: place after `before`.
    try {
      return generateKeyBetween(before ?? null, null);
    } catch {
      return generateKeyBetween(null, null);
    }
  }
}

export function keysBetween(before: string | null, after: string | null, n: number): string[] {
  try {
    return generateNKeysBetween(before, after, n);
  } catch {
    return generateNKeysBetween(before, null, n);
  }
}

/** Key for moving an item to `index` within `list` (which may contain the item). */
export function keyForIndex<T extends { id: string; sortKey: string }>(list: T[], itemId: string, index: number): string {
  const others = list.filter((x) => x.id !== itemId);
  const before = others[index - 1]?.sortKey ?? null;
  const after = others[index]?.sortKey ?? null;
  return keyBetween(before, after);
}

export const firstKey = (list: { sortKey: string }[]) => keyBetween(null, list[0]?.sortKey ?? null);
export const lastKey = (list: { sortKey: string }[]) => keyBetween(list[list.length - 1]?.sortKey ?? null, null);
